import { onValue, orderByChild, query, ref } from "firebase/database";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";

import { database } from "../../firebase/firebase";

export type BookingDirection = "past" | "upcoming";

interface Booking {
  id: string;
  [key: string]: any;
}

export function useBookings(direction: BookingDirection, initialLimit = 60) {
  const [pageSize, setPageSize] = useState(initialLimit);
  const [rawBookings, setRawBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const now = dayjs().valueOf();
    const bookingsRef = ref(database, "booking");

    // Para past: buscar todas as viagens e filtrar as que já passaram
    // Para upcoming: buscar todas as viagens e filtrar as futuras
    const bookingsQuery = query(bookingsRef, orderByChild("date"));

    const unsubscribe = onValue(bookingsQuery, (snapshot) => {
      if (!snapshot.exists()) {
        setRawBookings([]);
        setLoading(false);
        return;
      }

      const list: Booking[] = Object.entries(snapshot.val()).map(
        ([id, value]: [string, any]) => ({
          ...(value as Booking),
          id,
        })
      );

      // Filtrar baseado na direção: past = já passou, upcoming = ainda não passou
      const filtered = list.filter((booking) => {
        const bookingDate = booking.date;
        if (direction === "past") {
          return bookingDate < now; // Viagens que já passaram
        } else {
          return bookingDate > now; // Viagens futuras
        }
      });

      // Ordenar: past = do mais recente para o mais antigo, upcoming = do mais próximo para o mais longe
      const sorted = filtered.sort((a, b) => {
        if (direction === "past") {
          return b.date - a.date; // Mais recente primeiro
        } else {
          return a.date - b.date; // Mais próximo primeiro
        }
      });

      // Limitar o número de resultados
      const limited = sorted.slice(0, pageSize);
      setRawBookings(limited);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [direction, pageSize]);

  const grouped = useMemo(() => {
    return rawBookings.reduce<Record<string, Booking[]>>((acc, booking) => {
      const day = dayjs(booking.date).format("YYYY-MM-DD");
      if (!acc[day]) {
        acc[day] = [];
      }
      acc[day].push(booking);
      return acc;
    }, {});
  }, [rawBookings]);

  const loadMore = useCallback(() => {
    setPageSize((prev) => prev + initialLimit);
  }, [initialLimit]);

  return {
    loading,
    rawBookings,
    grouped,
    totalCount: rawBookings.length,
    loadMore,
  };
}
